//----- Component --------------------------------------------------------/
// Developer     : Guillermo Sedran
// Creation Date : 31/01/2025
// Version       : 1
// Description   : Panel principal dividido en cards con la patente y numero de observaciones de cada una
//
//------Changes ----------------------------------------------------------------------------------//
// Change Date   :
// Change by     :
// Change description:
//
// Change Date   :
// Change by     :
// Change description:
//
//========================================================================/

import { useState, useEffect, useMemo } from "react";
import { UrlApi } from "../../../services/apirest";
import Navbar from "../../Navbar";
import {
  Box,
  Card,
  CardContent,
  Dialog,
  Typography,
  TextField,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from "@mui/material";
import Grid from "@mui/material/Grid";

import ObservacionesPanelMantenimiento from "./ObservacionesPanelMantenimiento";

// ICONOS
import SearchIcon from "@mui/icons-material/Search";
import { logEventos } from "../../../services/services";

function PanelMantenimiento() {
  const [observaciones, setObservaciones] = useState([]);
  const [selectedVehiculo, setSelectedVehiculo] = useState(null);
  const [searchTerm, setSearchTerm] = useState(""); // Estado para el buscador
  const [sortBy, setSortBy] = useState("patente-asc"); // Estado para ordenamiento

  const obtenerObservaciones = () => {
    fetch(UrlApi + "/patentes_observaciones/paneldecontrol")
      .then((data) => data.json())
      .then((data) => {
        setObservaciones(data);
      });
  };

  useEffect(() => {
    obtenerObservaciones();
  }, []);

  // ---------------------------------------------

  const [dialogObservacion, setDialogObservacion] = useState(false);

  const handleOpenDialogObservacion = (vehiculo) => {
    setDialogObservacion(true);
    setSelectedVehiculo(vehiculo); // Almacena el vehículo seleccionado
  };

  const handleCloseDialogObservacion = () => {
    setDialogObservacion(false);
    setSelectedVehiculo(null); // Limpia el estado al cerrar
  };

  // ---------------------------------------------

  // Ordenamiento y búsqueda combinados
  const filteredObservaciones = useMemo(() => {
    return observaciones
      .slice() // Se crea una copia del array para evitar modificar el original
      .filter((vehiculo) =>
        // Filtra los vehículos cuya patente incluya el texto buscado (ignorando mayúsculas/minúsculas)
        vehiculo.patente.toLowerCase().includes(searchTerm.toLowerCase())
      )
      .sort((a, b) => {
        switch (sortBy) {
          case "patente-asc":
            // Ordena por patente en orden ascendente (A-Z)
            return a.patente.localeCompare(b.patente);
          case "patente-desc":
            // Ordena por patente en orden descendente (Z-A)
            return b.patente.localeCompare(a.patente);
          case "observaciones-asc":
            // Ordena por cantidad de observaciones de menor a mayor
            return (a.total_observaciones || 0) - (b.total_observaciones || 0);
          case "observaciones-desc":
            // Ordena por cantidad de observaciones de mayor a menor
            return (b.total_observaciones || 0) - (a.total_observaciones || 0);
          case "fecha-asc":
            // Ordena por fecha de última revisión en orden ascendente (más antigua primero)
            return (
              new Date(a.fecha_ultima_revision) -
              new Date(b.fecha_ultima_revision)
            );
          case "fecha-desc":
            // Ordena por fecha de última revisión en orden descendente (más reciente primero)
            return (
              new Date(b.fecha_ultima_revision) -
              new Date(a.fecha_ultima_revision)
            );
          default:
            return 0; // No realiza cambios si no hay criterio de ordenamiento
        }
      });
  }, [observaciones, searchTerm, sortBy]); // Se recalcula si cambian estos valores

  useEffect(() => {
    logEventos(
      "acceso al menú Panel de Mantenimiento",
      "Utilización de Vehículos",
      "Mantenimiento de Vehículos",
      localStorage.getItem("user")
    );
  }, []);

  return (
    <>
      <Navbar />
      <div
        style={{
          backgroundColor: "#F2F2F2",
          paddingTop: "5px",
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Typography
          variant="h4"
          align="center"
          sx={{
            backgroundColor: "#1976D2",
            color: "#ffffff",
            padding: "15px",
            fontSize: "2rem", // Tamaño normal en escritorio
            "@media (max-width: 600px)": {
              fontSize: "1.5rem", // Tamaño reducido en version mobile
            },
          }}
        >
          <strong>Mantenimiento de Vehículos</strong>
        </Typography>

        {/* Buscador y Ordenamiento */}
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap", // Para pantallas chicas
            justifyContent: "center",
            gap: 2,
            p: 2,
            backgroundColor: "#E3F2FD",
            borderRadius: 3, // bordes suaves para el contenedor
          }}
        >
          <TextField
            sx={{
              flex: 1,
              minWidth: 200,
              backgroundColor: "white",
              borderRadius: "12px",
              boxShadow: "0 2px 6px rgba(0,0,0,0.05)",
              transition: "all 0.2s ease",
              "&:hover": {
                boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
              },
              "& .MuiOutlinedInput-root": {
                borderRadius: "12px",
                "&.Mui-focused fieldset": {
                  borderColor: "#1976D2",
                  boxShadow: "0 0 0 3px rgba(25, 118, 210, 0.2)",
                },
              },
              "& .MuiInputBase-input::placeholder": {
                fontSize: "1rem",
                color: "#9e9e9e",
                "@media (max-width: 600px)": {
                  fontSize: "0.85rem",
                },
              },
            }}
            fullWidth
            variant="outlined"
            placeholder="Buscar patente..."
            size="small"
            autoComplete="off"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
          />

          <FormControl
            size="small"
            sx={{
              flex: 1,
              minWidth: 200,
              backgroundColor: "white",
              borderRadius: "12px",
              boxShadow: "0 2px 6px rgba(0,0,0,0.05)",
              transition: "all 0.2s ease",
              "&:hover": {
                boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
              },
              "& .MuiOutlinedInput-root": {
                borderRadius: "12px",
                "&.Mui-focused fieldset": {
                  borderColor: "#1976D2",
                  boxShadow: "0 0 0 3px rgba(25, 118, 210, 0.2)",
                },
              },
            }}
          >
            <InputLabel
              sx={{
                fontSize: "1rem",
                "@media (max-width: 600px)": {
                  fontSize: "0.85rem",
                },
              }}
            >
              Ordenar por
            </InputLabel>
            <Select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              label="Ordenar por"
              sx={{
                "& .MuiSelect-select": {
                  fontSize: "1rem",
                  paddingY: "8px",
                  "@media (max-width: 600px)": {
                    fontSize: "0.85rem",
                  },
                },
              }}
            >
              {[
                { value: "patente-asc", label: "Patente (A-Z)" },
                { value: "patente-desc", label: "Patente (Z-A)" },
                {
                  value: "observaciones-asc",
                  label: "Observaciones (Ascendente)",
                },
                {
                  value: "observaciones-desc",
                  label: "Observaciones (Descendente)",
                },
                { value: "fecha-asc", label: "Fecha de Revisión (Ascendente)" },
                {
                  value: "fecha-desc",
                  label: "Fecha de Revisión (Descendente)",
                },
              ].map((option) => (
                <MenuItem
                  key={option.value}
                  value={option.value}
                  sx={{
                    fontSize: "1rem",
                    "@media (max-width: 600px)": {
                      fontSize: "0.85rem",
                    },
                  }}
                >
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* Tarjetas */}
        <Grid
          container
          spacing={3}
          style={{ padding: "16px", marginBottom: "40px" }}
        >
          {filteredObservaciones.length > 0 ? (
            filteredObservaciones.map((vehiculo, index) => (
              <Grid
                item
                xs={12} // Móvil
                sm={6} // Tablet
                md={4} // Escritorio
                key={vehiculo.id_vehiculo}
              >
                {/* Columnas responsivas */}
                <Card
                  sx={{
                    backgroundColor: "#e3f2fd",
                    borderRadius: "16px", // Bordes redondeados
                    boxShadow: 4, // Sombra base
                    "&:hover": {
                      transform: "translateY(-5px)", // Elevación al pasar el mouse
                      boxShadow: "0px 10px 20px rgba(0, 0, 0, 0.15)", // Sombra más pronunciada en hover
                      outline: "2px solid #1E88E5",
                    },
                    transition: "transform 0.3s ease, box-shadow 0.3s ease", // Transición suave
                    cursor: "pointer",
                  }}
                  onClick={() => handleOpenDialogObservacion(vehiculo)}
                >
                  <CardContent sx={{ p: 0, mb: -2 }}>
                    {/* Sección de título con fondo */}
                    <Box
                      sx={{
                        backgroundColor: "#2196F3",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        padding: "8px 12px", // Ajustamos los espacios internos
                        borderRadius: "8px 8px 0 0",
                      }}
                    >
                      <Typography
                        variant="h6"
                        fontWeight="bold"
                        sx={{
                          fontSize: "1rem",
                          color: "white",
                          marginLeft: "16px", // Mueve la patente más a la derecha
                        }}
                      >
                        {vehiculo.patente}
                      </Typography>

                      <Typography
                        variant="body2"
                        sx={{
                          color: "white",
                          marginRight: "16px", // Mueve "Última revisión" más a la izquierda
                        }}
                      >
                        Última revisión:{" "}
                        {new Date(
                          vehiculo.fecha_ultima_revision
                        ).toLocaleDateString()}
                      </Typography>
                    </Box>

                    <Grid
                      container
                      sx={{
                        padding: "8px 0",
                      }}
                    >
                      <Grid
                        item
                        xs={4}
                        sx={{
                          textAlign: "center",
                        }}
                      >
                        <Typography
                          variant="h4"
                          fontWeight="bold"
                          color="error"
                          sx={{
                            userSelect: "none", // Evita la selección de texto
                          }}
                        >
                          {vehiculo.observaciones_frecuencia || "0"}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="textSecondary"
                          sx={{
                            userSelect: "none", // Evita la selección de texto
                          }}
                        >
                          Tiempo
                        </Typography>
                      </Grid>
                      <Grid item xs={4} sx={{ textAlign: "center" }}>
                        <Typography
                          variant="h4"
                          fontWeight="bold"
                          color="error"
                          sx={{
                            userSelect: "none", // Evita la selección de texto
                          }}
                        >
                          {vehiculo.observaciones_frecuencia_km || "0"}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="textSecondary"
                          sx={{
                            userSelect: "none", // Evita la selección de texto
                          }}
                        >
                          Kilometraje
                        </Typography>
                      </Grid>
                      <Grid item xs={4} sx={{ textAlign: "center" }}>
                        <Typography
                          variant="h4"
                          fontWeight="bold"
                          color="primary"
                          sx={{
                            userSelect: "none", // Evita la selección de texto
                          }}
                        >
                          {vehiculo.total_observaciones || "0"}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="textSecondary"
                          sx={{
                            userSelect: "none", // Evita la selección de texto
                          }}
                        >
                          Total
                        </Typography>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            ))
          ) : (
            <Grid
              item
              xs={12}
              display="flex"
              justifyContent="center"
              alignItems="center"
              height="100%"
            >
              <Card
                sx={{
                  backgroundColor: "#F0F4F8",
                  borderRadius: "14px", // Bordes redondeados
                  boxShadow: 4,
                  p: 2,
                }}
              >
                <Typography variant="body1">
                  No hay datos disponibles
                </Typography>
              </Card>
            </Grid>
          )}
        </Grid>

        <Dialog
          fullScreen
          open={dialogObservacion}
          onClose={handleCloseDialogObservacion}
        >
          <ObservacionesPanelMantenimiento
            vehiculo={selectedVehiculo}
            handleClose={handleCloseDialogObservacion}
          />
        </Dialog>
      </div>
    </>
  );
}

export default PanelMantenimiento;
